import { gaPushEvent } from "../base.analytics";
import type { RoomDiscoveryChannel } from "./invite.analytics";
import { OBJECTION_WORDING_VERSION } from "../../../domains/partners/partnerObjection";

/**
 * The partner objection and the room welcome sheet
 * (docs/partner-objection.md, PRD section 5). The opposition rate at signup is
 * `partner_objection_answered{surface=signup, objected=true}` divided by
 * `partner_objection_shown{surface=signup}`.
 */
enum Event {
  PARTNER_OBJECTION_SHOWN = "partner_objection_shown",
  PARTNER_OBJECTION_ANSWERED = "partner_objection_answered",
  ROOM_WELCOME_SHOWN = "room_welcome_shown",
  ROOM_WELCOME_COMPLETED = "room_welcome_completed",
  ROOM_INFO_OPENED = "room_info_opened",
}

/** What the contact line of a welcome or info sheet says. */
export type ContactLineKind = "conditional" | "informs" | "confirms" | "none";

/** The objection screen at signup (O1) reached the screen. */
export function gaEventPartnerObjectionShown(payload: { surface: "signup" }) {
  gaPushEvent(Event.PARTNER_OBJECTION_SHOWN, {
    surface: payload.surface,
    wording_version: OBJECTION_WORDING_VERSION,
  });
}

/**
 * The user confirmed the objection screen, or changed a switch in the
 * settings, and the API saved it. `organisationId` null is the global scope.
 */
export function gaEventPartnerObjectionAnswered(payload: {
  surface: "signup" | "settings";
  objected: boolean;
  organisationId: string | null;
}) {
  gaPushEvent(Event.PARTNER_OBJECTION_ANSWERED, {
    surface: payload.surface,
    objected: payload.objected,
    scope: payload.organisationId === null ? "global" : "organisation",
    organisation_id: payload.organisationId,
  });
}

/** The welcome sheet opened after a join (O3). */
export function gaEventRoomWelcomeShown(payload: {
  roomId: string;
  roomDiscoveryChannel: RoomDiscoveryChannel | null;
  hasClientDisclaimer: boolean;
  contactLine: Exclude<ContactLineKind, "conditional">;
}) {
  gaPushEvent(Event.ROOM_WELCOME_SHOWN, {
    room_id: payload.roomId,
    room_discovery_channel: payload.roomDiscoveryChannel,
    has_client_disclaimer: payload.hasClientDisclaimer,
    contact_line: payload.contactLine,
  });
}

/** The welcome sheet closed: its button, or the cross and the swipe. */
export function gaEventRoomWelcomeCompleted(payload: {
  roomId: string;
  exit: "discover" | "closed";
  disclaimerExpanded: boolean;
}) {
  gaPushEvent(Event.ROOM_WELCOME_COMPLETED, {
    room_id: payload.roomId,
    exit: payload.exit,
    disclaimer_expanded: payload.disclaimerExpanded,
  });
}

/** The info button of the "Supported by" badge opened the room info sheet. */
export function gaEventRoomInfoOpened(payload: {
  roomId: string;
  isMember: boolean;
  contactLine: ContactLineKind;
}) {
  gaPushEvent(Event.ROOM_INFO_OPENED, {
    room_id: payload.roomId,
    is_member: payload.isMember,
    contact_line: payload.contactLine,
  });
}
